/* ZUZU shared data - modalities, plans, defaults. */

import { ZZ_BENEFITS, ZZ_GUIDES } from "./guides";

// Five modalities (zuzuforyou.com lists a nordic sauna, cold plunge, normatec compression,
// red light therapy, and InBody scans - we keep all five).
const ZZ_MODALITIES_RAW = [
  {
    id: "plunge",
    name: "cold plunge",
    tag: "01 · cold",
    quote: "cold water. warm feelings.",
    quoteTexture: "texture-calm-water.ce772b76.webp",
    eyebrow: "cold",
    temp: "7 & 12°C",
    timeLabel: "",
    timeMin: 3,
    unitLabel: "tubs",
    units: 2,
    short: "two dunk xl tubs, kept clean and kept cold.",
    blurb:
      "breathe deeply. embrace the cold. leave feeling clearer, calmer, and more awake.",
    // home modality card uses the cleanly-cut dunk plunge bath shot.
    homePhoto: "/redesign/assets/imagery/cp-dunk-bath-cut.bcee0a75.webp",
    // responsive candidates for the full-size photo (equipment index,
    // modality spread and detail hero all render it via a plain <img>).
    photoSrcSet:
      "/redesign/assets/imagery/cp-dunk-bath-cut-720.421245ab.webp 720w, /redesign/assets/imagery/cp-dunk-bath-cut.bcee0a75.webp 1462w",
    // Dedicated small re-encode of homePhoto for the home-grid card only —
    // see sauna's homeThumb comment.
    homeThumb: "/redesign/assets/imagery/cp-plunge-home.16a975f1.webp",
    // right-sized candidates for the home-grid card (Lighthouse "improve
    // image delivery"); homeThumb is the larger pick for wide/retina cards.
    homeSrcSet:
      "/redesign/assets/imagery/cp-dunk-bath-cut-576.b1f5392a.webp 576w, /redesign/assets/imagery/cp-plunge-home.16a975f1.webp 652w",
    // equipment photo slot - user drops a real photo of the tub onto this and
    // it persists across loads. compression already has equipment images;
    // the other four wait for the user to provide them.
    equipmentSlot: "equipment-plunge",
    equipmentPlaceholder: "drop a photo of the cold plunge tub here",
    photos: [
      "/redesign/assets/imagery/cp-tub-hero-cut.3883342f.webp",
      "/redesign/assets/imagery/cp-woman-room.c3502dab.webp",
      "/redesign/assets/imagery/cold-plunge-water-closeup.c5e20975.webp",
    ],
    faqs: [
      [
        "how cold is it really?",
        "two tubs - 7°C and 12°C. a 24/7 filtration system keeps the water clear without chlorine.",
      ],
      [
        "what if I can't last three minutes?",
        "get out whenever. one minute counts. progress lives in coming back, not in toughing it out.",
      ],
      [
        "is the cold safe?",
        "for healthy adults, yes. if you're pregnant, have a heart condition, or take blood-pressure medication, check with a doctor first.",
      ],
      [
        "plunge before or after the sauna?",
        "both work. most members alternate - sauna, plunge, sauna, plunge.",
      ],
    ],
    mood: "calm",
    tint: "var(--zuzu-zumthor)",
    deep: "var(--zuzu-ship-cove)",
    accent: "var(--zuzu-ship-cove)",
  },
  {
    id: "sauna",
    name: "sauna",
    tag: "02 · heat",
    quote: "made for slow mornings and better afternoons.",
    quoteTexture: "texture-slow-sand.9132862e.webp",
    eyebrow: "heat",
    temp: "90°C",
    timeLabel: "",
    timeMin: 45,
    unitLabel: "people",
    units: 8,
    short: "a true nordic sauna",
    blurb:
      "heat that reaches beyond the surface. a simple ritual to help your body recover, reset, and feel its best.",
    equipmentSlot: "equipment-sauna",
    equipmentPlaceholder: "drop a photo of the nordic sauna cabin here",
    // The home-grid card renders at ~323×431 CSS px — a dedicated,
    // appropriately-sized re-encode of photos[0] so that tiny card doesn't
    // ship the full detail-page/hero-size original (Lighthouse "improve
    // image delivery" finding). Detail page, browse index and the room
    // hero keep using the full-size file via photos[0]/RM_HERO.
    homeThumb: "/redesign/assets/imagery/sauna-interior-v2-768.dce3a1d6.webp",
    photoSrcSet:
      "/redesign/assets/imagery/sauna-interior-v2-tall-480.324b22dc.webp 480w, /redesign/assets/imagery/sauna-interior-v2-tall.89733994.webp 710w",
    // The equipment-index card is a wide 380×200 strip — give it the
    // landscape frame instead of the 4:5 crop the detail/spread frames use.
    indexSrcSet:
      "/redesign/assets/imagery/sauna-interior-v2-480.af2d6802.webp 480w, /redesign/assets/imagery/sauna-interior-v2-768.dce3a1d6.webp 768w, /redesign/assets/imagery/sauna-interior-v2.c5bb520d.webp 1206w",
    homeSrcSet:
      "/redesign/assets/imagery/sauna-interior-v2-480.af2d6802.webp 480w, /redesign/assets/imagery/sauna-interior-v2-768.dce3a1d6.webp 768w",
    photos: [
      "/redesign/assets/imagery/sauna-interior-v2.c5bb520d.webp",
      "/redesign/assets/imagery/sauna-exterior-front.e3564d90.webp",
      "/redesign/assets/imagery/sauna-exterior-side.850cdd1f.webp",
    ],
    faqs: [
      [
        "how hot does it actually get?",
        "90°C - a proper nordic heat. dry and intense, so most members do ten to fifteen minutes a round and step out to cool down. flip the timer inside for another fifteen.",
      ],
      [
        "can I bring my phone in?",
        "please don't. lockers at the entrance. the heat will eat it, and your sauna will be quieter.",
      ],
      [
        "what should I wear?",
        "a towel. the cabin is private and single-use - one cabin, one body.",
      ],
      [
        "how often is too often?",
        "daily is fine for most members. if you're training hard, twice a day on rest days is common.",
      ],
    ],
    mood: "slow",
    tint: "var(--zuzu-vanilla)",
    deep: "#5d4a36",
    accent: "var(--zuzu-old-rose)",
  },
  {
    id: "comp",
    name: "compression",
    tag: "04 · flow",
    quote: "the art of doing nothing.",
    quoteTexture: "texture-beige-fabric.bd4a8687.webp",
    // beige fabric is light — use a soft warm wash instead of the dark ink overlay
    quoteOverlay:
      "linear-gradient(180deg, rgba(244,237,229,0.20) 0%, rgba(213,197,180,0.42) 100%)",
    quoteLight: true,
    eyebrow: "flow",
    temp: null,
    timeLabel: "15-30 min",
    timeMin: 20,
    unitLabel: "",
    units: 1,
    short: "normatec premier boots, the futon, charging port.",
    blurb:
      "because recovery has a pace too. your next PB will thank you for this.",
    // compression is the one room where we have actual equipment imagery -
    // pre-fill the slot with the futon photo; user can swap if they want.
    equipmentSlot: "equipment-comp",
    equipmentPlaceholder: "drop a photo of the boots + futon here",
    // See sauna's homeThumb comment — same reasoning, dedicated small
    // re-encode of photos[0] for the home-grid card only.
    homeThumb:
      "/redesign/assets/imagery/compression-boots-flatlay-home.8eb221c4.webp",
    photoSrcSet:
      "/redesign/assets/imagery/compression-boots-flatlay-720.2341d02f.webp 720w, /redesign/assets/imagery/compression-boots-flatlay.11d7f816.webp 1084w",
    homeSrcSet:
      "/redesign/assets/imagery/compression-boots-flatlay-home-576.0bc94ee1.webp 576w, /redesign/assets/imagery/compression-boots-flatlay-home.8eb221c4.webp 672w",
    photos: [
      "/redesign/assets/imagery/compression-boots-flatlay.11d7f816.webp",
      "/redesign/assets/imagery/comp-woman-couch-cut.458e6ae5.webp",
      "/redesign/assets/equipment/compression-futon.e054c30a.webp",
    ],
    faqs: [
      [
        "is this just for athletes?",
        "no. anyone who sits all day, flies often, or stands for hours benefits as much as a marathon runner.",
      ],
      [
        "how strong should I set it?",
        "LEVEL 1 to 7 on the battery pack. firm and rhythmic, never painful - most members sit around the middle and go higher after a hard effort. ZONEBOOST adds extra compression in one zone.",
      ],
      [
        "can I bring a book?",
        "yes - that's the design. hands free, headphones on, lights low.",
      ],
      [
        "is it safe with varicose veins?",
        "usually, but ask your gp first. blood thinners are a no without their okay.",
      ],
    ],
    // three "how members use this room" scenarios - visible on the
    // /modalities deep-spread for compression
    scenarios: [
      {
        name: "recline & read",
        time: "20 min",
        body: "lower the futon, zip in the boots, and put your phone face-down. twenty minutes to yourself - most don't make it through a chapter.",
        photo: "/redesign/assets/equipment/compression-futon.e054c30a.webp",
        slotId: "comp-scenario-recline",
        placeholder: "photo of a member reclined on the futon with the boots",
      },
      {
        name: "after a workout",
        time: "20–30 min",
        body: "pair with the cold plunge or the sauna first, then the boots, full pressure, eyes closed.",
        photo: "/redesign/assets/equipment/compression-product.8c685ed6.webp",
        slotId: "comp-scenario-run",
        placeholder: "photo of trainers + boots on the futon",
      },
      {
        name: "on a flight day",
        time: "20 min",
        body: "before a flight, an hour on the futon undoes a day of sitting. after a flight, it undoes the flight.",
        photo: "/redesign/assets/imagery/meditation-room.99b0cf26.webp",
        slotId: "comp-scenario-flight",
        placeholder: "photo of a member with the boots, late afternoon light",
      },
    ],
    mood: "human",
    tint: "var(--zuzu-oyster-pink)",
    deep: "#4a2a2b",
    accent: "var(--zuzu-coral-tree)",
  },
  {
    id: "red",
    name: "red light",
    tag: "03 · light",
    quote: "good for your body. easy on your eyes.",
    quoteTexture: "texture-human-terracotta.3309cde0.webp",
    eyebrow: "light",
    temp: null,
    timeLabel: "10-30 min",
    timeMin: 10,
    unitLabel: "panels",
    units: 2,
    short: "2x red light & near-infrared (NIR) panels with multiple settings",
    blurb:
      "the most effortless ten minutes of your recovery routine. lie back, close your eyes, and let the light do the work.",
    // home + equipment cards use the studio shot of the panel on the brand
    // coral background. the vertical panel-on-dark shot keeps the room reading
    // as a tall panel; user can drop their own onto the slot.
    homePhoto: "/redesign/assets/imagery/red-light-on-dark.07ba482a.webp",
    photoSrcSet:
      "/redesign/assets/imagery/red-light-on-dark-720.9756ce9e.webp 720w, /redesign/assets/imagery/red-light-on-dark.07ba482a.webp 1086w",
    // See sauna's homeThumb comment — dedicated small re-encode of homePhoto
    // for the home-grid card only; homePhoto itself stays full-size for the
    // other (larger) contexts that also read it.
    homeThumb: "/redesign/assets/imagery/red-light-on-dark-home.82d9cb10.webp",
    homeSrcSet:
      "/redesign/assets/imagery/red-light-on-dark-home-576.f27d303c.webp 576w, /redesign/assets/imagery/red-light-on-dark-home.82d9cb10.webp 675w",
    equipmentSlot: "equipment-red",
    equipmentPlaceholder: "drop a photo of the red-light panels here",
    photos: [
      "/redesign/assets/imagery/red-light-on-dark.07ba482a.webp",
      "/redesign/assets/imagery/red-light-panel.492ff1a8.webp",
    ],
    faqs: [
      [
        "what wavelengths do you use?",
        "660 nm for skin and 850 nm for deep tissue. two panels - a face panel for facial skin, a body panel for larger areas.",
      ],
      [
        "will it tan me?",
        "no - there's no uv. red light is a different mechanism entirely. you'll feel warmth, no burn.",
      ],
      [
        "how soon will I see results?",
        "skin softens within a week. recovery effects are quieter - felt, not seen.",
      ],
      [
        "can I go straight from the sauna?",
        "yes. some members prefer it - body already warm, less time to dress and undress.",
      ],
    ],
    mood: "human",
    tint: "#7F4446",
    deep: "#4a2a2b",
    accent: "var(--zuzu-coral-tree)",
  },
  {
    id: "inbody",
    name: "inbody scan",
    tag: "05 · monitor",
    quote: "your favourite five minutes.",
    quoteTexture: "texture-natural-leaf.7d68a3ce.webp",
    eyebrow: "monitor",
    temp: null,
    timeLabel: "5 min",
    timeMin: 5,
    unitLabel: "station",
    units: 1,
    short: "a five-minute body composition read.",
    blurb:
      "step on, hold the handles, and see what's really changing. muscle, hydration, balance, and progress - measured, not assumed.",
    equipmentSlot: "equipment-inbody",
    equipmentPlaceholder: "drop a photo of the inbody scanner here",
    // home modality card uses the scanner-only shot; the rest of the site
    // (modality detail, browse) keeps the woman-on-scanner cutout
    homePhoto: "/redesign/assets/imagery/inbody-120-catalogue.a69dba24.webp",
    photoSrcSet:
      "/redesign/assets/imagery/inbody-120-catalogue-720.03c47bec.webp 720w, /redesign/assets/imagery/inbody-120-catalogue-1152.63c0deaf.webp 1152w, /redesign/assets/imagery/inbody-120-catalogue.a69dba24.webp 1920w",
    // Dedicated small re-encode of homePhoto for the home-grid card only —
    // see sauna's homeThumb comment.
    homeThumb:
      "/redesign/assets/imagery/inbody-120-catalogue-home.92406310.webp",
    homeSrcSet:
      "/redesign/assets/imagery/inbody-120-catalogue-home-576.70319505.webp 576w, /redesign/assets/imagery/inbody-120-catalogue-home.92406310.webp 900w",
    photos: [
      "/redesign/assets/imagery/inbody-120-catalogue.a69dba24.webp",
      "/redesign/assets/imagery/inbody-120-real.a69dba24.webp",
    ],
    faqs: [
      [
        "how often should I scan?",
        "quarterly is plenty. weekly is noise - body composition doesn't move on a weekly timescale.",
      ],
      [
        "is it actually accurate?",
        "it closely matches hospital-grade body scans, with no radiation the way a DEXA scan has. good enough to track real change, not good enough to fixate on.",
      ],
      [
        "what does it actually measure?",
        "body water, lean mass, fat mass, and segmental balance - left vs right arm and leg.",
      ],
      [
        "can I see last quarter's?",
        'yes. every read syncs to your profile under "your reads". no spreadsheets needed.',
      ],
    ],
    mood: "natural",
    tint: "var(--zuzu-green-white)",
    deep: "var(--zuzu-kelp)",
    accent: "var(--zuzu-limed-ash)",
  },
];

// Red light sits before compression across every room listing (home grid,
// /rooms index, footer, app) so the colour rhythm flows cold → heat → light → flow → monitor.
// Decorative texture backdrops ship a 768w mobile re-encode alongside the
// full-size original. They render full-bleed on desktop (where even the
// original is stretched) but only ~740 physical px on phones. The choice is
// made once at load — textures are soft backdrops, so a resize/rotation
// serving the "wrong" size is invisible.
const ZZ_TEXTURE_MOBILE =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(max-width: 760px)").matches;
// Explicit desktop -> mobile (768w) pairing. These filenames are
// content-hashed for immutable caching, so each variant's hash is
// independent - a "-768.webp" string-suffix derivation (the previous
// approach) can no longer find the right file.
const ZZ_TEXTURE_MOBILE_MAP = {
  "texture-calm-water.ce772b76.webp": "texture-calm-water-768.bccff52f.webp",
  "texture-slow-sand.9132862e.webp": "texture-slow-sand-768.bda8711f.webp",
  "texture-beige-fabric.bd4a8687.webp":
    "texture-beige-fabric-768.a1b45b44.webp",
  "texture-human-terracotta.3309cde0.webp":
    "texture-human-terracotta-768.bc396fa3.webp",
  "texture-natural-leaf.7d68a3ce.webp":
    "texture-natural-leaf-768.9547160a.webp",
};
export const zzTextureUrl = (file) =>
  "/redesign/assets/imagery/" +
  (ZZ_TEXTURE_MOBILE ? ZZ_TEXTURE_MOBILE_MAP[file] || file : file);

export const ZZ_MODALITIES = ["plunge", "sauna", "red", "comp", "inbody"].map(
  (id) => {
    const m = ZZ_MODALITIES_RAW.find((x) => x.id === id);
    const benefits = ZZ_BENEFITS[id];
    // `why` is read by three surfaces (the app's benefits tab, the /rooms
    // long-read spread and the room's own page) - deriving it from the one
    // benefits list keeps them from drifting apart.
    return {
      ...m,
      benefits,
      guide: ZZ_GUIDES[id],
      why: benefits.items,
    };
  },
);

// Pricing tiers - three real products.
// 1. Pay as you go (credits, R100 each · 30 min off-peak / 15 min peak).
// 2. Monthly membership - off-peak only (R899); off-peak hours vary by day.
// 3. Private room booking (up to 10 people; R1,000/hr off-peak, R2,000/hr peak,
//    or a 30-min slot for R600 off-peak / R1,200 peak).
export const ZZ_TIERS = [
  {
    id: "drop",
    name: "pay as you go",
    eyebrow: "no commitment",
    // What cancelling gives back is a policy, not a bullet: the card names the
    // one fact and opens the rest (see cancelPolicyCopy / CancelPolicyModal).
    cancelPolicy: true,
    // Same R100 - different time inside depending on when you visit.
    subOptions: [
      {
        id: "off-peak",
        name: "off-peak",
        price: "R100",
        per: "/ credit",
        duration: "30 minutes",
        // The off-peak window is a different one on saturday and runs all day
        // on sunday, so naming the weekday hours here read as the whole rule.
        // The peak/off-peak table sits directly below these cards.
        time: "hours vary by day",
        window: "30 minutes · off-peak hours vary by day",
        accent: "var(--zuzu-limed-ash)",
      },
      {
        id: "peak",
        name: "peak",
        price: "R100",
        per: "/ credit",
        duration: "15 minutes",
        time: "hours vary by day",
        window: "15 minutes · peak hours vary by day",
        accent: "var(--zuzu-ship-cove)",
      },
    ],
    // The single most important message on the page.
    creditExplainer: {
      lead: "1 credit = 30 min off-peak · 15 min peak",
      body: "use it in any of our five rooms. a full recovery circuit - sauna, plunge, compression, red light - takes about an hour, or roughly two credits. off-peak/peak hours differ by day - sunday is off-peak all day - and a booking spanning both is charged pro rata.",
    },
    keynote: "pay only for what you use",
    blurb: "",
    features: [
      "access to all five rooms",
      "use any or all rooms in one session",
      "1 credit = 30 min off-peak · 15 min peak (hours vary by day; sun is off-peak all day)",
      "no monthly fee, no contract",
      "credits last three years",
      "gift cards available - send someone credits",
    ],
    cta: "buy credits",
  },
  {
    id: "monthly",
    name: "off-peak membership",
    eyebrow: "off-peak unlimited",
    featured: true,
    badge: "best value",
    // A toggle between the two ways to pay - matches the other two cards.
    subOptions: [
      {
        id: "annual",
        name: "12 months",
        price: "R899",
        per: "/ month",
        window: "this month + 11 more, paid up front · best value",
        accent: "var(--zuzu-ship-cove)",
      },
      {
        id: "monthly",
        name: "monthly",
        price: "R1,299",
        per: "/ month",
        window: "runs to month end · pro-rated when you join",
        accent: "var(--zuzu-ship-cove)",
      },
    ],
    sub: "unlimited off-peak visits, every day - 9am–4pm weekdays, 1pm–5pm saturday, all day sunday.",
    keynote: "unlimited off-peak, every day",
    blurb: "",
    features: [
      "unlimited off-peak visits - 9am–4pm weekdays, 1pm–5pm saturday, all day sunday",
      "access to the full circuit",
      "no commitment - you pay for one month at a time, and it never renews on its own",
      "the most affordable unlimited recovery in Cape Town",
    ],
    cta: "become a member",
    footnote:
      "private room bookings take priority - if the room is privately booked, unlimited members need to choose another time.",
  },
  {
    id: "private",
    name: "private room",
    eyebrow: "group bookings",
    cancelPolicy: true,
    badge: "whole space",
    subOptions: [
      {
        id: "off-peak",
        name: "off-peak",
        price: "R1,000",
        per: "/ hour",
        window:
          "up to 10 people · from 30 min (R600) · or 10 credits an hour",
        accent: "var(--zuzu-limed-ash)",
      },
      {
        id: "peak",
        name: "peak",
        price: "R2,000",
        per: "/ hour",
        window:
          "up to 10 people · from 30 min (R1,200) · or 20 credits an hour",
        accent: "var(--zuzu-ship-cove)",
      },
    ],
    sub: "reserve the whole space for your group - birthdays, training partners, team off-sites. pay by card, with credits, or a mix of the two. off-peak/peak hours vary by day (weekdays 9am–4pm off-peak, saturday 1pm–5pm off-peak, sunday off-peak all day); a booking spanning both windows is charged pro rata.",
    keynote: "the whole space · just your group, up to 10",
    blurb:
      "reserve the whole room for your group. birthdays, training partners, team off-sites.",
    creditPricing: [
      {
        window: "off-peak",
        credits: 10,
        time: "30 min = 6 credits",
      },
      {
        window: "peak",
        credits: 20,
        time: "30 min = 12 credits",
      },
    ],
    features: [
      "up to 10 people",
      "guaranteed private use of the whole room",
      "play your own music through the room",
      "all five circuits",
      "peak/off-peak hours vary by day - sat/sun differ from weekdays, and sunday is off-peak all day; bookings spanning both windows are charged pro rata",
      "cancelling is free more than 48 hrs ahead - after that, what comes back depends on how much notice you give",
      "gift cards available - send someone credits",
    ],
    cta: "reserve the room",
  },
];

// The five rooms - reference labels for the pricing page.
export const ZZ_ROOM_LABELS = [
  "nordic sauna",
  "cold plunge",
  "compression boots",
  "red-light therapy",
];

// Live-status per room. `free` is a placeholder only until ZuzuRedesign.tsx
// overwrites it in place with the real count from get_live_capacity at
// mount (see the merge there) — every real render a member sees uses the
// live value, never this default. `total` and `temp` are static equipment
// facts (room capacity, water/sauna temperature) and match the real capacity
// map in ZuzuRedesign.tsx's availability() (plunge:2, sauna:8, comp:1,
// red:1, inbody:1) and ZZ_SAUNAS/ZZ_PLUNGES below.
//
// There used to be a `sub` field here too ('until 12:45' for comp, 'until
// 1pm tomorrow' for red) — specific-sounding "next available" times that
// were never actually derived from real bookings and were never read by any
// screen. Removed rather than "fixed", since there's no real per-room next-
// availability computation to back it with.
export const ZZ_LIVE_DEFAULTS = {
  plunge: { free: 1, total: 2, temp: "7 & 12°C" },
  sauna: { free: 2, total: 8, temp: "90°C" },
  comp: { free: 1, total: 1 },
  red: { free: 1, total: 1 },
  inbody: { free: 1, total: 1 },
};

// The sauna is a single nordic cabin, up to eight people at a time.
export const ZZ_SAUNAS = [
  {
    id: "nordic",
    name: "nordic",
    label: "nordic sauna",
    temp: "90°C",
    free: 1,
    total: 8,
  },
];

// Cold plunge has two tubs members can pick between — 7°C and 12°C.
export const ZZ_PLUNGES = [
  { id: "7", label: "7°C", free: 0, total: 1 },
  { id: "12", label: "12°C", free: 1, total: 1 },
];

// Color palette options for the Tweaks panel
export const ZZ_PALETTES = [
  ["#834D4E", "#E6CDCC", "#F4EDE5"], // human   - coral / oyster / linen
  ["#828D62", "#E1E4D8", "#F4EDE5"], // natural - sage / faint sage / linen
  ["#D5C5B4", "#F4EDE5", "#FAF8F4"], // slow    - vanilla / linen / spring wood
  ["#676D88", "#D9DBE3", "#F4EDE5"], // calm    - ship cove / zumthor / linen
];
export const ZZ_PALETTE_MOOD = ["human", "natural", "slow", "calm"];

// Marketing-site page id -> real route, shared with ZuzuRedesign.tsx and every
// internal <a> in site.jsx (with onClick still doing the fast client-side
// transition) - a bare onClick with no href leaves the link uncrawlable,
// since there's nothing for a crawler to follow.
export const ZZ_SITE_PATHS = {
  home: "/",
  about: "/about",
  modalities: "/equipment",
  "modality:plunge": "/experience/plunge",
  "modality:sauna": "/experience/sauna",
  "modality:comp": "/experience/compress",
  "modality:red": "/experience/redlight",
  "modality:inbody": "/experience/scan",
  // Public availability + slot picker, before there's an account.
  book: "/book",
  pricing: "/pricing",
  "signup:monthly": "/signup/membership",
  "signup:drop": "/signup/credits",
  "signup:private": "/signup/private",
  contact: "/contact",
  legal: "/legal",
  app: "/dashboard",
  // Sign-in sheet (WaAuth) — the header's login button links here.
  auth: "/auth",
  // Group / event booking pages — separate top-level routes (not part of
  // this component's page switcher), keyed by their own slug so onNav's
  // navigate(SITE_PATHS[p]) pattern works for them the same as any other
  // page id. See ZZ_EVENT_LINKS below for the homepage/footer listing.
  "corporate-team-building": "/corporate-team-building",
  "birthday-party": "/birthday-party",
  "bachelorette-party": "/bachelorette-party",
  "team-recovery": "/team-recovery",
  "date-night": "/date-night",
  "gift-a-session": "/gift-a-session",
  "corporate-gift-cards": "/corporate-gift-cards",
  "group-day-out": "/group-day-out",
  "run-club-recovery": "/run-club-recovery",
  "marathon-recovery": "/marathon-recovery",
  "hyrox-recovery": "/hyrox-recovery",
  "crossfit-recovery": "/crossfit-recovery",
};

// Private-room / group-booking pages — orphaned otherwise (no nav/footer
// linked into them), reachable only via the sitemap or a direct URL.
// Rendered as a homepage section (SEvents) and a footer column so a real
// visitor can actually find them, not just a crawler.
export const ZZ_EVENT_LINKS = [
  { id: "bachelorette-party", label: "bachelorette party" },
  { id: "birthday-party", label: "birthday party" },
  { id: "date-night", label: "date night" },
  { id: "corporate-team-building", label: "corporate team building" },
  { id: "team-recovery", label: "sports team recovery" },
  { id: "group-day-out", label: "group day out" },
  { id: "run-club-recovery", label: "run club recovery" },
  { id: "marathon-recovery", label: "marathon recovery" },
  { id: "hyrox-recovery", label: "hyrox recovery" },
  { id: "crossfit-recovery", label: "crossfit recovery" },
  { id: "gift-a-session", label: "gift a session" },
  { id: "corporate-gift-cards", label: "corporate gift cards" },
];
